import { ShopItemAdapter } from "../adapters/types";
import { z } from "zod";

import { ItemType } from "@/types/enums/item-type";
import {
  MembershipPurchaseLineInput,
  MembershipShopCartLine,
} from "@/types/shop";

const lineSchema = z.object({
  kind: z.literal("membership"),
  itemType: z.literal(ItemType.MEMBERSHIP),
  itemId: z.string().min(1, "Membership is invalid."),
  quantity: z.literal(1),
  unitPriceCents: z.number().int().nonnegative("Membership price is invalid."),
  expectedUnitPriceCents: z.number().int().nonnegative().optional(),
});

const validate = (line: MembershipShopCartLine): string | null => {
  const result = lineSchema.safeParse(line);
  if (!result.success) {
    return result.error.issues[0]?.message ?? "Membership line is invalid.";
  }
  return null;
};

const serializeForCheckout = (
  line: MembershipShopCartLine,
): MembershipPurchaseLineInput => ({
  itemType: ItemType.MEMBERSHIP,
  itemId: line.itemId,
  quantity: 1,
  expectedUnitPriceCents: line.expectedUnitPriceCents ?? line.unitPriceCents,
  expectedRecurringPriceCents: line.snapshot.membership?.recurringPrice ?? null,
  expectedBillingFrequency: line.snapshot.membership?.billingFrequency ?? null,
  expectedAutoRenew: line.snapshot.membership?.autoRenew ?? null,
  expectedShopBillingStart: line.snapshot.membership?.shopBillingStart ?? null,
});

const normalizeLine = (
  line: MembershipShopCartLine,
): MembershipShopCartLine => ({ ...line, quantity: 1 });

export const membershipAdapter: ShopItemAdapter<
  MembershipShopCartLine,
  MembershipPurchaseLineInput
> = {
  type: ItemType.MEMBERSHIP,
  validate,
  serializeForCheckout,
  normalizeLine,
};
